/** iOS uses webkit messagehandlers:
 *  https://developer.apple.com/documentation/webkit/wkusercontentcontroller
 *  Android uses addJavaScriptInterface which depends on a global object being injected which holds all the communication methods. In this case the global object is named: AndroidInterface
 *  https://developer.android.com/develop/ui/views/layout/webapps/webview#BindingJavaScript
 **/

export enum DeviceEnvironment {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  UNKNOWN_DEVICE = 'UNKOWN_DEVICE',
}

// Notify mobile app when a SPA page change takes place
export enum PageTypesForNative {
  LANDING,
  PRODUCT,
  PRODUCT_APPLICATION,
  SESSION_EXPIRED,
  PAGE_NOT_FOUND,
  ERROR,
}

export interface Android {
  setPageChange: (pageType: PageTypesForNative) => void;
  exitWebApp: (url: string) => void;
  confirm: (url: string) => void;
  messageReceived: (url: string) => void;
  exitWebAppToExternalVMPage: (url: string) => void;
}

export interface Webkit {
  webAppLoaded: {
    postMessage: () => void;
  };
  setPageChange: {
    postMessage: (pageType: PageTypesForNative) => void;
  };
  exitWebApp: {
    postMessage: (url: string) => void;
  };
  confirm: (url: string) => void;
  messageReceived: {
    postMessage: (url: string) => void;
  };
  exitWebAppToExternalVMPage: {
    postMessage: (url: string) => void;
  };
}

declare global {
  interface Window {
    AndroidInterface: Android;
    webkit: {
      messageHandlers: Webkit;
    };
  }
}

export const mobileOS = () => {
  if (window.AndroidInterface) {
    return DeviceEnvironment.ANDROID;
  }
  if (window.webkit && window.webkit.messageHandlers) {
    return DeviceEnvironment.IOS;
  }
  return DeviceEnvironment.UNKNOWN_DEVICE;
};

export const isNativeView = (window: Window = global.window): boolean => {
  if (mobileOS() === DeviceEnvironment.ANDROID) {
    return true;
  }
  if (mobileOS() === DeviceEnvironment.IOS) {
    // eslint-disable-next-line
    return window?.webkit?.messageHandlers.hasOwnProperty('webAppLoaded') && typeof window?.webkit?.messageHandlers?.webAppLoaded.postMessage === 'function';
    // return Object.hasOwn(window?.webkit?.messageHandlers, 'webAppLoaded') && typeof window?.webkit?.messageHandlers?.webAppLoaded.postMessage === 'function';
  }
  return false;
};

// Tell the native app that we have changed page
export const setPageChange = (pageType: PageTypesForNative) => {
  try {
    if (mobileOS() === DeviceEnvironment.ANDROID) {
      window.AndroidInterface.setPageChange(pageType);
    } else if (window.webkit) {
      window.webkit.messageHandlers.setPageChange.postMessage(pageType);
    }
  } catch (err) {
    console.error(err);
  }
};

// exit webapp for Third Party application workflow
export const exitWebApp = (url: string) => {
  try {
    if (mobileOS() === DeviceEnvironment.ANDROID) {
      window.AndroidInterface.exitWebApp(url);
    } else if (window.webkit) {
      window.webkit.messageHandlers.exitWebApp.postMessage(url);
    }
  } catch (err) {
    console.error(err);
  }
};

export const messageReceived = (url: string) => {
  try {
    if (mobileOS() === DeviceEnvironment.ANDROID) {
      window.AndroidInterface.messageReceived(url);
    } else if (window.webkit) {
      window.webkit.messageHandlers.messageReceived.postMessage(url);
    }
  } catch (err) {
    console.error(err);
  }
};

// exit webapp for external VM product page
export const exitWebAppToExternalVMPage = (url: string) => {
  try {
    if (mobileOS() === DeviceEnvironment.ANDROID) {
      window.AndroidInterface.exitWebAppToExternalVMPage(url);
    } else if (window.webkit) {
      window.webkit.messageHandlers.exitWebAppToExternalVMPage.postMessage(url);
    }
  } catch (err) {
    console.error(err);
  }
};
